import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogSurface,
  DialogTitle,
  Button,
  mergeClasses,
  Subtitle1,
  Caption1,
  Label,
} from '@fluentui/react-components';

import { Dismiss20Regular } from '@fluentui/react-icons';
import { UrlInputFluent } from '@bingads-webui-campaign-react/url-input-fluent-v2';
import { ClipchampPlayer } from '@bingads-webui-campaign-react/clipchamp-player';
import { useTemplateDetailsModalStyles } from './style';

export const TemplateDetailsModal = ({
  template,
  show,
  onSelectTemplate,
  setCopilotFinalUrl,
  handleOpenChange,
  onClose,
  i18n,
  getAssetCountInfo,
  loadClipchampPlayer,
}) => {
  const [finalUrlError, setFinalUrlError] = useState('');
  const [url, setUrl] = useState('');

  /* istanbul ignore next */
  const onGetStartedButton = useCallback(() => {
    if (finalUrlError) {
      return;
    }

    if (!url) {
      setFinalUrlError(i18n.getString(_TL_('Please enter a valid URL.')));
      return;
    }

    onSelectTemplate({ template });
  }, [onSelectTemplate, template, setFinalUrlError, i18n, finalUrlError, url]);

  const DismissIcon = useMemo(() => <Button icon={<Dismiss20Regular />} aria-label="Cancel" onClick={onClose} />, [onClose]);

  const classes = useTemplateDetailsModalStyles();

  return (
    <Dialog
      open={show}
      onOpenChange={handleOpenChange}
      className={mergeClasses('template-details-dialog', classes.dialog)}
    >
      <DialogSurface className={mergeClasses('template-details-modal', classes.dialog)} aria-label="Template details.">
        <DialogBody>
          <DialogTitle action={DismissIcon} />
          <DialogContent>
            <div className={classes.dialogContentContainer}>
              <div className={classes.videoContainer}>
                <ClipchampPlayer videoUrl={template.TemplatePreviewUrl} poster={template.TemplateThumbnailUrl} width="600px" loadClipchampPlayer={loadClipchampPlayer} i18n={i18n} />
              </div>
              <div className={classes.detailsContainer}>
                <Subtitle1 role="heading" aria-level="1">{template.LocalizedTemplateName || template.TemplateName}</Subtitle1>
                <div>
                  {getAssetCountInfo(template)}
                </div>
                <div>
                  {i18n.getString(_TL_('Duration: {{seconds}} seconds'), { seconds: template.Duration })}
                </div>
                <div className={classes.textGroup}>
                  <div>{template.LocalizedTemplateDescription || template.TemplateDescription}</div>
                  <Label weight="semibold">{i18n.getString(_TL_('Enter a URL and we\'ll use it to generate relevant assets for your template.'))} </Label>
                  <UrlInputFluent
                    url={url}
                    onChange={(urlErrors, newUrl) => {
                      setFinalUrlError(urlErrors[0]);
                      setCopilotFinalUrl(newUrl);
                      setUrl(newUrl);
                    }}
                    textFieldAriaLabel={i18n.getString(_TL_('Enter your URL'))}
                    dropdownOptionAriaLabel={i18n.getString(_TL_('Select protocol'))}
                    i18n={i18n}
                    additionalProperties={{
                      placeholder: i18n.getString(_TL_('Enter your URL')),
                      id: 'url-input',
                    }}
                  />
                  {finalUrlError && <Caption1 className={classes.errorMsg} role="alert" aria-label={finalUrlError}>{finalUrlError}</Caption1>}
                </div>
                <Button onClick={onGetStartedButton} appearance="primary" className="get-started-button">{i18n.getString(_TL_('Get started'))}</Button>
              </div>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

TemplateDetailsModal.propTypes = {
  template: PropTypes.shape({
    TemplateId: PropTypes.string.isRequired,
    TemplateName: PropTypes.string.isRequired,
    TemplateDescription: PropTypes.string.isRequired,
    Duration: PropTypes.number,
    AspectRatio: PropTypes.string,
    Themes: PropTypes.arrayOf(PropTypes.string),
    NumberOfImages: PropTypes.number,
    NumberOfText: PropTypes.number,
    TemplateThumbnailUrl: PropTypes.string.isRequired,
    TemplatePreviewUrl: PropTypes.string.isRequired,
    NumberOfLogos: PropTypes.string.isRequired,
  }).isRequired,
  show: PropTypes.bool.isRequired,
  setCopilotFinalUrl: PropTypes.func.isRequired,
  onSelectTemplate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  getAssetCountInfo: PropTypes.func.isRequired,
  loadClipchampPlayer: PropTypes.func.isRequired,
};
